/**
 * The three gaits the player picks (02 §13, ROUND7 §2): run, crawl and the crouched walk (sneak), each a toggle or
 * held (08 §4). Pure.
 *
 * The sim decides what wins when more than one is asked for - crawl and run over sneak - but a toggled gait left
 * latched underneath another comes back unasked the moment the other ends. So the latches keep out of each
 * other's way: pressing crawl or run drops a toggled sneak (the brief's rule), and pressing sneak drops a toggled
 * crawl or run - pressing X while crawling means "get up into a crouch", not "nothing happens". Run and crawl
 * leave each other alone, as they always have.
 */

import { Latch } from './latch';

/** One gait control this tick: down now, and went down since the last tick. */
export interface GaitControl {
  held: boolean;
  pressed: boolean;
}

/** Which gaits are toggles (the rest are held). */
export interface GaitToggles {
  runToggle: boolean;
  crawlToggle: boolean;
  sneakToggle: boolean;
}

/** What the command asks for this tick. */
export interface Gaits {
  run: boolean;
  crawl: boolean;
  sneak: boolean;
}

export class GaitLatches {
  private readonly runLatch = new Latch();
  private readonly crawlLatch = new Latch();
  private readonly sneakLatch = new Latch();

  /** Once per sim tick. */
  update(run: GaitControl, crawl: GaitControl, sneak: GaitControl, toggles: GaitToggles): Gaits {
    // Sneak first, so a crawl or run pressed on the same tick has the last word, as it does in the sim.
    if (sneak.pressed) {
      if (toggles.runToggle) this.runLatch.reset();
      if (toggles.crawlToggle) this.crawlLatch.reset();
    }
    this.sneakLatch.update(sneak.held, sneak.pressed, toggles.sneakToggle);
    const r = this.runLatch.update(run.held, run.pressed, toggles.runToggle);
    const c = this.crawlLatch.update(crawl.held, crawl.pressed, toggles.crawlToggle);
    if ((run.pressed || crawl.pressed) && toggles.sneakToggle) this.sneakLatch.reset();
    return { run: r, crawl: c, sneak: this.sneakLatch.value };
  }

  /** The crawl toggle is input-side state; a downed ranger's forced crawl is the sim's. */
  get crawling(): boolean {
    return this.crawlLatch.value;
  }

  /** The sneak latch as of the last update. */
  get sneaking(): boolean {
    return this.sneakLatch.value;
  }
}
